import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PART_SIZE = 4 * 1024 * 1024;
const FIELDS = ["phone", "first_name", "last_name", "company", "country", "language", "timezone",
  "email", "product_interest", "source", "external_ref"];
const MODES = [
  { value: "skip", label: "Skip duplicates" },
  { value: "fill_empty", label: "Update empty fields only" },
  { value: "update_all", label: "Update all mapped fields" },
  { value: "membership_only", label: "Add existing contact to this list only" },
];

export function ImportWizard({ list, onClose }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("skip");
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!list) {
      setFile(null); setUploadId(null); setPreview(null); setJobId(null); setJob(null);
      setUploadPercent(0); setMapping({}); setBusy(false);
    }
  }, [list]);

  useEffect(() => {
    if (!jobId) return undefined;
    const poll = async () => {
      try {
        const data = await get(`/import-jobs/${jobId}`);
        setJob(data);
        if (["completed", "failed", "cancelled", "blocked", "paused"].includes(data.status)) {
          clearInterval(timer.current);
          queryClient.invalidateQueries({ queryKey: ["contact-lists"] });
          queryClient.invalidateQueries({ queryKey: ["import-jobs"] });
        }
      } catch (e) {
        /* keep polling; transient errors are expected while the worker restarts */
      }
    };
    poll();
    timer.current = setInterval(poll, 2000);
    return () => clearInterval(timer.current);
  }, [jobId, queryClient]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
      const { data: session } = await api.post("/uploads/init", {
        list_id: list.id, filename: file.name, size: file.size, total_parts: totalParts,
      });
      setUploadId(session.id);
      for (let index = 0; index < totalParts; index += 1) {
        const blob = file.slice(index * PART_SIZE, (index + 1) * PART_SIZE);
        const form = new FormData();
        form.append("file", blob, `${file.name}.part${index}`);
        // eslint-disable-next-line no-await-in-loop
        await api.put(`/uploads/${session.id}/parts/${index}`, form);
        setUploadPercent(Math.round(((index + 1) / totalParts) * 100));
      }
      const data = await get(`/uploads/${session.id}/preview`);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      toast.success(`Uploaded ${totalParts} part(s) to object storage`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/uploads/${uploadId}/complete`, {
        mapping, duplicate_mode: mode, has_header: true,
        defaults: { country: list.country || "AL", language: list.language || "sq" },
        estimated_rows: preview?.estimated_rows,
      });
      setJobId(data.job_id);
      toast.success("Import queued — processing runs in the background");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const control = async (action) => {
    try {
      await api.post(`/import-jobs/${jobId}/${action}`);
      toast.success(`Import ${action} requested`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totals = job?.result?.totals || job?.progress || {};

  return (
    <Dialog open={!!list} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl bg-white" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle>Import CSV into {list?.name}</DialogTitle>
        </DialogHeader>

        {!preview && !jobId ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>CSV file (processed in the background, never in the browser)</Label>
              <input
                data-testid="import-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            {file ? (
              <p className="text-xs text-slate-500" data-testid="import-file-meta">
                {file.name} · {(file.size / 1e6).toFixed(1)} MB ·{" "}
                {Math.max(1, Math.ceil(file.size / PART_SIZE))} upload part(s)
              </p>
            ) : null}
            {uploadPercent > 0 && uploadPercent < 100 ? (
              <div className="space-y-1">
                <Progress value={uploadPercent} data-testid="upload-progress" />
                <p className="text-xs text-slate-500">Uploading {uploadPercent}%</p>
              </div>
            ) : null}
            <DialogFooter>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={!file || busy} onClick={upload} data-testid="import-upload-button">
                {busy ? "Uploading…" : "Upload to storage"}
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {preview && !jobId ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-500" data-testid="import-preview-meta">
              Detected {preview.headers.length} columns · approx. {preview.estimated_rows.toLocaleString()} rows
              (estimated from a sample, the full file is parsed server-side)
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {FIELDS.map((field) => (
                <div key={field} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2 py-1.5">
                  <span className="text-xs font-medium text-slate-700">
                    {field}{field === "phone" ? " *" : ""}
                  </span>
                  <Select value={mapping[field] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [field]: v === "__none__" ? undefined : v })}>
                    <SelectTrigger className="h-8 w-40" data-testid={`map-${field}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__none__">Not mapped</SelectItem>
                      {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label>Duplicate handling</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="duplicate-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={!mapping.phone || busy} onClick={startImport} data-testid="import-start-button">
                Start background import
              </Button>
            </DialogFooter>
          </div>
        ) : null}

        {jobId ? (
          <div className="space-y-3" data-testid="import-progress-panel">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-800">
                Status: <span data-testid="import-job-status">{job?.status || "queued"}</span>
              </span>
              <span className="text-xs text-slate-500" data-testid="import-job-processed">
                {(job?.progress?.processed || 0).toLocaleString()} rows ·{" "}
                {job?.chunks_done || 0} chunks
              </span>
            </div>
            <Progress value={job?.progress?.percent || 0} data-testid="import-progress" />
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {[["Created", totals.created], ["Existing", totals.existing],
                ["Invalid", totals.invalid], ["DNC", totals.dnc]].map(([label, value]) => (
                <div key={label} className="rounded-md border border-slate-200 px-2 py-1.5">
                  <p className="kpi-label">{label}</p>
                  <p className="text-sm font-medium text-slate-800">{value ?? 0}</p>
                </div>
              ))}
            </div>
            {job?.error ? <p className="text-xs text-rose-600" data-testid="import-job-error">{job.error}</p> : null}
            <div className="flex flex-wrap gap-2">
              {["processing", "queued"].includes(job?.status) ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => control("pause")} data-testid="import-pause">Pause</Button>
                  <Button size="sm" variant="outline" className="text-rose-600" onClick={() => control("cancel")} data-testid="import-cancel">Cancel</Button>
                </>
              ) : null}
              {["paused", "failed", "blocked"].includes(job?.status) ? (
                <Button size="sm" variant="outline" onClick={() => control("resume")} data-testid="import-resume">Resume</Button>
              ) : null}
              {job?.error_rows ? (
                <Button size="sm" variant="outline" asChild data-testid="import-error-report">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/import-jobs/${jobId}/error-report`} target="_blank" rel="noreferrer">
                    {job.error_rows} invalid rows
                  </a>
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" onClick={onClose} data-testid="import-close">Close</Button>
            </div>
            <p className="text-xs text-slate-500">
              You can navigate away — the import keeps running in the worker process and you will
              be notified when it finishes.
            </p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
